import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";

import { sentryLogs, timezone } from "@/utils";
import { DEFAULT_API_ERROR_MESSAGE } from "@/utils/Constants";
import { RAZOR_KEY_ID } from "@/config";
import theme from "@/foundation/Theme";
import logo from "@/static/logo-mh-razorpay.png";

import { useAppDispatch, useAppSelector } from "@/hooks";
import * as actions from "@/redux/LandingPageReducer";

const usePaymentForm = (props) => {
  const { paymentSuccessPath } = props || {};

  const router = useRouter();
  const dispatch = useAppDispatch();

  const [razorPayError, setRazorPayError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onSuccessCallback = useCallback(
    (inputs, response) => {
      const {
        name,
        countryCode,
        mobile,
        email,
        slug,
        urlsArguments,
        promoCode,
        purchasedPlanSlug,
        dermaType,
        pageType,
      } = inputs || {};
      const coupon_code = (promoCode || "")?.toUpperCase();
      const apiData = {
        name,
        phone_prefix: countryCode || "+91",
        mobile,
        ...(email && { email }),
        ...(dermaType && { derma_type: dermaType }),
        package_slug: slug,
        transaction_id: response.razorpay_payment_id,
        amount_paid: response?.amount,
        timezone,
        payment_mode: "razorpay",
        urls_arguments: urlsArguments,
        is_callback_request: false,
        is_payment_completed: true,
        ...(coupon_code && { coupon_code: coupon_code }),
        ...(purchasedPlanSlug && {
          purchased_plan_slug: purchasedPlanSlug,
        }),
        ...(pageType && { page_type: pageType }),
      };

      dispatch(
        actions.postPackageSave({ router, data: apiData, paymentSuccessPath })
      );
    },
    [dispatch]
  );

  const onDismiss = useCallback(
    (inputs) => {
      const { name, countryCode, mobile, email, slug, urlsArguments } =
        inputs || {};

      return dispatch(
        actions.saveLead({
          data: {
            name: name,
            phone: mobile,
            phone_prefix: countryCode ? countryCode : "+91",
            ...(email && { email }),

            urls_arguments: urlsArguments,
            timezone,
            payment_mode: "razorpay",
            payment_stage: "payment_cancel",
            package_slug: slug,
          },
        })
      );
    },
    [dispatch]
  );

  const initiateRazorPayPayment = useCallback((razorPayOptions) => {
    const onLoad = () => {
      let rzp1 = new window.Razorpay(razorPayOptions);
      rzp1.open();
    };
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.defer = true;
      document.body.appendChild(script);
      script.onload = onLoad;

      script.onerror = () => {
        setRazorPayError(DEFAULT_API_ERROR_MESSAGE);
        sentryLogs(
          "error",
          "Razorpay library not loaded hence Razorpay is undefined."
        );
      };
    } else {
      onLoad();
    }
  }, []);

  const openPaymentModal = useCallback(
    (inputs, totalPrice, currency, desc, callback = () => {}) => {
      callback();

      const {
        name,
        countryCode,
        mobile,
        email,
        goal,
        purchasedPlanSlug,
        pageType,
        urlsArguments,
        promoCode,
        dermaType,
      } = inputs || {};

      const razorPayOptions = {
        key: RAZOR_KEY_ID,
        amount: totalPrice * 100,
        currency: currency,
        name: name,
        description: desc,
        image: logo,
        secondaryBuy: false,
        handler: function (response) {
          if (response.razorpay_payment_id) {
            onSuccessCallback(inputs, { ...response, amount: totalPrice });
          }
        },
        prefill: {
          name,
          contact: (countryCode || "+91") + mobile,
          email: email || "",
        },
        notes: {
          name,
          primary_contact: (countryCode || "+91") + mobile,
          ...(email && {
            email: email,
          }),
          package_slug: goal,
          ...(purchasedPlanSlug && {
            purchased_plan_slug: purchasedPlanSlug,
          }),
          ...(urlsArguments && {
            urls_arguments: urlsArguments,
          }),
          ...(pageType && {
            page_type: pageType,
          }),
          ...(promoCode && { coupon_code: promoCode }),
          ...(dermaType && { derma_type: dermaType }),
          timezone,
        },
        theme: {
          color: theme.colorsV2.shyft.purple,
        },
        modal: {
          ondismiss: async function () {
            await onDismiss(inputs);
          },
        },
      };
      initiateRazorPayPayment(razorPayOptions);
    },
    [initiateRazorPayPayment, onSuccessCallback, onDismiss]
  );

  const openPaymentPage = useCallback(
    (inputs, data) => {
      const totalPrice = data?.price;
      const currency = data?.currency;
      const desc = inputs?.purchasedPlanSlug || inputs?.goal;
      const callback = () => {};
      // const callback = () => dispatch(resetStatusValues());

      openPaymentModal(inputs, totalPrice, currency, desc, callback);
    },
    [dispatch, openPaymentModal]
  );

  return {
    razorPayError,
    openPaymentPage,
  };
};

export default usePaymentForm;
